import { Group, Stack, Text } from "@mantine/core"
import { InfoBox, InformationItem, RingProgress } from "@tfg/ui"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { ActivityProgressIcon } from "~/pages/SupplierPortal/TraceabilityRequests/components/ActivityProgressIcon"
import {
  findParentReferenceInTree,
  ItemTypeInformation,
} from "~/pages/SupplierPortal/TraceabilityRequests/components/OperationItemInformation"
import type {
  OperationItem,
  RequestWithOpItemFromOperationTree,
} from "~/pages/SupplierPortal/TraceabilityRequests/utils"
import { useGetOperations } from "~/utils/operations"

export const MaskedOperationActivity = ({
  operationItem,
  request,
}: {
  operationItem: OperationItem
  request: RequestWithOpItemFromOperationTree
}) => {
  const operation = useGetOperations()[operationItem.activityName]
  const { t } = useTranslation("traceability")
  const parentReference = useMemo(
    () =>
      findParentReferenceInTree(
        request.original.item,
        operationItem.item.id,
        undefined
      ),
    [operationItem.item.id, request.original.item]
  )

  return (
    <Group
      h="100%"
      align="flex-start"
      py="var(--tfg-organism-padding-y)"
      px="var(--tfg-organism-padding-x)"
      w="100%"
      data-nodeid={operationItem.nodeId}
      pos="relative"
    >
      <Stack key={operationItem.nodeId} w="100%" justify="flex-start" h="100%">
        <Stack gap="xs" w="100%">
          <Group wrap="nowrap" align="center">
            <RingProgress
              size={40}
              roundCaps
              styles={{
                label: {
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                },
              }}
              thickness={4}
              sections={[{ value: 0, color: "var(--mantine-color-gray-1)" }]}
              label={
                <ActivityProgressIcon
                  progress={0}
                  activityName={operationItem.activityName}
                  size={20}
                  isTransferred
                />
              }
            />
            <Text fw={700} size="sm">
              {operation?.name}
            </Text>
          </Group>
          <ItemTypeInformation item={operationItem.item} />
          {parentReference && (
            <InformationItem title={t("item.partOf")} value={parentReference} />
          )}
        </Stack>
        <InfoBox
          variant="info"
          title={t("operation.otherFacility.title")}
          message={t("operation.otherFacility.message")}
        />
      </Stack>
    </Group>
  )
}
